import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import CityPicker from '../../components/CityPicker';
import {
  Avatar, Chip, Empty, Input, Loading,
} from '../../components/Ui';
import { Reveal, stagger } from '../../components/Motion';
import { EXPERIENCE_FILTERS } from '../../constants';

/**
 * Everyone who switched "looking for a job" on, for anyone who might hire
 * them. Ride, cab and parcel partners are kept out - the server refuses them.
 */
const SeekerCard = ({ person, onClick }) => (
  <button type="button" className="card sh-sm seeker-card" onClick={onClick}>
    <Avatar name={person.name} uri={person.avatar} size={52} />
    <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
      <div className="t-title clamp-1">{person.name || 'KAAMWALA member'}</div>
      <div className="t-small clamp-1">{person.jobTitle || 'Open to work'}</div>
      <div className="row wrap" style={{ gap: 6, marginTop: 8 }}>
        <span className="pill">{person.experienceYears ? `${person.experienceYears} yr experience` : 'Fresher'}</span>
        {person.city ? <span className="pill">📍 {person.city}</span> : null}
        {person.resume ? <span className="pill">📄 Resume</span> : null}
      </div>
      {(person.skills || []).length ? (
        <div className="t-tiny clamp-1" style={{ marginTop: 6 }}>{person.skills.join(' · ')}</div>
      ) : null}
    </div>
  </button>
);

const JobSeekersPage = () => {
  const navigate = useNavigate();
  const [people, setPeople] = useState([]);
  const [search, setSearch] = useState('');
  const [place, setPlace] = useState({ city: '', district: '', state: '' });
  const [experience, setExperience] = useState('');
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await api.user.seekers({
        search: search || undefined,
        city: place.city || undefined,
        experience: experience === '' ? undefined : experience,
        limit: 30,
      });
      setPeople(res.data || []);
      setBlocked('');
    } catch (err) {
      setPeople([]);
      setBlocked(err.status === 403 ? err.message : '');
    } finally {
      setLoading(false);
    }
  }, [search, place.city, experience]);

  useEffect(() => {
    const timer = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  return (
    <div>
      <PageHeader
        title="Job seekers"
        subtitle={`${people.length} open to work${place.city ? ` in ${place.city}` : ''}`}
      />

      <div className="card sh-sm" style={{ marginBottom: 16 }}>
        <Input
          icon="🔍"
          placeholder="Search by name, work or skill"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <div className="split-2" style={{ gap: 12 }}>
          <CityPicker
            label="City"
            value={place}
            onChange={({ city, district, state }) => setPlace({ city, district, state })}
          />
          <div>
            <label className="field-label">Experience</label>
            <div className="row wrap" style={{ gap: 8 }}>
              {EXPERIENCE_FILTERS.map((e) => (
                <Chip key={e.value || 'any'} label={e.label} active={experience === e.value} onClick={() => setExperience(e.value)} />
              ))}
            </div>
            <div className="t-tiny" style={{ marginTop: 6 }}>We show people with that much experience or more.</div>
          </div>
        </div>

        {place.city || experience !== '' ? (
          <button type="button" className="link-btn" style={{ marginTop: 10 }}
            onClick={() => { setPlace({ city: '', district: '', state: '' }); setExperience(''); }}>
            Clear filters
          </button>
        ) : null}
      </div>

      {loading ? <Loading /> : blocked ? (
        <Empty emoji="🛵" title="Not for ride partners" text={blocked} />
      ) : !people.length ? (
        <Empty emoji="🧑‍🔧" title="Nobody here yet" text="Nobody matching this is open to work right now. Try a wider search." />
      ) : (
        <div className="grid grid-cards">
          {people.map((person, i) => (
            <Reveal key={person._id} delay={stagger(i, 45)}>
              <SeekerCard person={person} onClick={() => navigate(`/people/${person._id}`)} />
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
};

export default JobSeekersPage;
